import { useState, useEffect, useRef, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Save, RotateCcw, Eye, Plus, Trash2, Type, Image as ImageIcon,
  Minus, Square, Table, ChevronDown, FileText, Search, ZoomIn, ZoomOut,
  Copy, Check,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { pedirExclusao } from '@/lib/confirmarExclusao'

// =================== Tipos ===================
type Bloco =
  | { id: string; tipo: 'texto'; x: number; y: number; w: number; h: number;
      conteudo: string; fonte?: { familia?: string; tamanho?: number };
      cor?: string; alinhamento?: 'left' | 'center' | 'right' }
  | { id: string; tipo: 'imagem'; x: number; y: number; w: number; h: number;
      fonte_imagem?: string }
  | { id: string; tipo: 'linha'; x: number; y: number; w: number; h?: number;
      cor?: string; espessura?: number }
  | { id: string; tipo: 'retangulo'; x: number; y: number; w: number; h: number;
      cor?: string; preenchimento?: string }
  | { id: string; tipo: 'tabela'; x: number; y: number; w: number; h: number;
      colunas: Array<{ label: string; campo: string; largura: number; align?: string; formato?: string }>;
      fonte?: { familia?: string; tamanho?: number };
      cor_cabecalho?: string; cor_texto_cabecalho?: string }

interface Spec {
  page: { size: string; orientation: 'portrait' | 'landscape'; margin: number }
  blocos: Bloco[]
}

interface SpecResp {
  spec: Spec
  template_nome: string
  logo_data: string | null
}

// =================== Constantes ===================
const PAGINA_LARGURA_CM = 21.0  // A4
const PAGINA_ALTURA_CM = 29.7

const CORES_TIPO: Record<string, string> = {
  texto: '#0ea5e9',
  imagem: '#8b5cf6',
  linha: '#64748b',
  retangulo: '#10b981',
  tabela: '#f97316',
}

// =================== Componente principal ===================
interface TemplateItem { id: number; nome: string; ativo: boolean; tem_spec: boolean; updated_at: string | null }

const TITULOS_TIPO: Record<string, string> = {
  pedido: 'Editor de Layout do Pedido',
  mapa: 'Editor de Layout do Mapa de Separação',
  romaneio: 'Editor de Layout do Romaneio',
}

export default function LayoutPedidoPage({ tipoDoc = 'pedido' }: { tipoDoc?: 'pedido' | 'mapa' | 'romaneio' } = {}) {
  // Endpoints genéricos /api/layout/{tipo_doc}/...
  const apiBase = `/layout/${tipoDoc}`
  const qc = useQueryClient()
  const [spec, setSpec] = useState<Spec | null>(null)
  const [logoData, setLogoData] = useState<string | null>(null)
  const [selecionado, setSelecionado] = useState<string | null>(null)
  const [zoom, setZoom] = useState(1.0)   // 1.0 = 1cm:25px
  const [buscaCampo, setBuscaCampo] = useState('')
  const [templateAtual, setTemplateAtual] = useState<string>('')

  const { data: templates = [] } = useQuery<TemplateItem[]>({
    queryKey: ['layout-doc-templates', tipoDoc],
    queryFn: async () => (await api.get(`${apiBase}/templates`)).data,
  })

  const { data: specResp, isLoading } = useQuery<SpecResp & { ativo: boolean }>({
    queryKey: ['layout-doc-spec', tipoDoc, templateAtual],
    queryFn: async () => (await api.get(`${apiBase}/spec`,
      { params: templateAtual ? { template: templateAtual } : {} })).data,
  })
  const { data: campos = [] } = useQuery<Array<{ campo: string; descricao: string }>>({
    queryKey: ['layout-doc-campos', tipoDoc],
    queryFn: async () => (await api.get(`${apiBase}/campos`)).data,
  })

  useEffect(() => {
    if (specResp) {
      setSpec(specResp.spec)
      setLogoData(specResp.logo_data)
      if (!templateAtual) setTemplateAtual(specResp.template_nome)
    }
  }, [specResp])

  const salvar = useMutation({
    mutationFn: async () => (await api.put(`${apiBase}/spec`, {
      spec, template_nome: templateAtual || specResp?.template_nome || 'Padrão', logo_data: logoData,
    })).data,
    onSuccess: () => { toast.success('Layout salvo')
      qc.invalidateQueries({ queryKey: ['layout-doc-spec', tipoDoc] })
      qc.invalidateQueries({ queryKey: ['layout-doc-templates', tipoDoc] }) },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao salvar'),
  })

  const ativarTemplate = useMutation({
    mutationFn: async (nome: string) => (await api.put(`${apiBase}/templates/${encodeURIComponent(nome)}/ativar`)).data,
    onSuccess: () => { toast.success('Template ativado')
      qc.invalidateQueries({ queryKey: ['layout-doc-templates', tipoDoc] })
      qc.invalidateQueries({ queryKey: ['layout-doc-spec', tipoDoc] }) },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  const criarTemplate = useMutation({
    mutationFn: async (data: { nome: string; copiar_de?: string }) =>
      (await api.post(`${apiBase}/templates`, data)).data,
    onSuccess: (_r, v) => { toast.success(`Template "${v.nome}" criado`)
      setTemplateAtual(v.nome)
      qc.invalidateQueries({ queryKey: ['layout-doc-templates', tipoDoc] })
      qc.invalidateQueries({ queryKey: ['layout-doc-spec', tipoDoc] }) },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  const deletarTemplate = useMutation({
    mutationFn: async (nome: string) => (await api.delete(`${apiBase}/templates/${encodeURIComponent(nome)}`)).data,
    onSuccess: () => { toast.success('Template removido'); setTemplateAtual('')
      qc.invalidateQueries({ queryKey: ['layout-doc-templates', tipoDoc] })
      qc.invalidateQueries({ queryKey: ['layout-doc-spec', tipoDoc] }) },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  const visualizar = async () => {
    if (!spec) return
    const t = toast.loading('Gerando preview...')
    try {
      const token = localStorage.getItem('pain_token')
      const r = await fetch(`/api/layout/${tipoDoc}/preview-spec`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ spec, logo_data: logoData, template_nome: 'preview' }),
      })
      toast.dismiss(t)
      if (!r.ok) { toast.error('Erro ao gerar preview'); return }
      const blob = await r.blob()
      window.open(URL.createObjectURL(blob), '_blank')
    } catch { toast.dismiss(t); toast.error('Erro') }
  }

  const restaurar = useMutation({
    mutationFn: async () => (await api.delete(`${apiBase}/spec`)).data,
    onSuccess: () => { toast.success('Layout restaurado pro padrão'); qc.invalidateQueries({ queryKey: ['layout-doc-spec', tipoDoc] }) },
  })

  const atualizarBloco = useCallback((id: string, patch: Partial<Bloco>) => {
    setSpec(s => s ? { ...s, blocos: s.blocos.map(b => b.id === id ? { ...b, ...patch } as Bloco : b) } : s)
  }, [])

  const deletarBloco = (id: string) => {
    if (!confirm('Remover bloco?')) return
    setSpec(s => s ? { ...s, blocos: s.blocos.filter(b => b.id !== id) } : s)
    if (selecionado === id) setSelecionado(null)
  }

  const adicionarBloco = (tipo: Bloco['tipo']) => {
    if (!spec) return
    const id = `bloco_${Date.now()}`
    let novo: Bloco
    if (tipo === 'texto') {
      novo = { id, tipo: 'texto', x: 5, y: 20, w: 8, h: 0.5, conteudo: 'Novo texto',
        fonte: { familia: 'Helvetica', tamanho: 9 }, cor: '#000000', alinhamento: 'left' }
    } else if (tipo === 'imagem') {
      novo = { id, tipo: 'imagem', x: 5, y: 20, w: 3, h: 2, fonte_imagem: 'empresa.logo' }
    } else if (tipo === 'linha') {
      novo = { id, tipo: 'linha', x: 1, y: 15, w: 19, h: 0.05, cor: '#000000', espessura: 0.5 }
    } else if (tipo === 'retangulo') {
      novo = { id, tipo: 'retangulo', x: 5, y: 18, w: 6, h: 2, cor: '#000000', preenchimento: '#f1f5f9' }
    } else {
      novo = { id, tipo: 'tabela', x: 1, y: 20, w: 19, h: 0, fonte: { familia: 'Helvetica', tamanho: 8 },
        cor_cabecalho: '#0284c7', cor_texto_cabecalho: '#ffffff',
        colunas: [
          { label: 'Cód', campo: 'produto.codigo', largura: 1.5, align: 'left' },
          { label: 'Descrição', campo: 'produto.descricao', largura: 9, align: 'left' },
          { label: 'Qtde', campo: 'item.quantidade', largura: 1.5, align: 'right' },
          { label: 'Total', campo: 'item.total', largura: 2, align: 'right', formato: 'moeda' },
        ] }
    }
    setSpec(s => s ? { ...s, blocos: [...s.blocos, novo] } : s)
    setSelecionado(id)
  }

  if (isLoading || !spec) return <div className="p-8 text-center text-ink-400">Carregando editor...</div>

  const blocoSelecionado = spec.blocos.find(b => b.id === selecionado) || null

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col bg-ink-50">
      {/* Toolbar */}
      <div className="border-b border-ink-200 bg-white px-4 py-2 flex items-center gap-2 flex-wrap">
        <FileText size={18} className="text-ink-600" />
        <h1 className="font-bold">{TITULOS_TIPO[tipoDoc] || TITULOS_TIPO.pedido}</h1>
        <TemplateSeletor
          templates={templates}
          atual={templateAtual}
          onTrocar={setTemplateAtual}
          onAtivar={(n) => ativarTemplate.mutate(n)}
          onCriar={(n, copiar) => criarTemplate.mutate({ nome: n, copiar_de: copiar })}
          onDeletar={(n) => pedirExclusao({ descricao: `o template "${n}"`, onConfirm: () => deletarTemplate.mutate(n) })}
        />
        <AdicionarBlocoMenu onAdicionar={adicionarBloco} />
        <div className="flex-1" />
        <div className="flex items-center gap-1 border border-ink-200 rounded-md px-1">
          <button onClick={() => setZoom(z => Math.max(0.4, z - 0.1))} className="p-1 hover:bg-ink-100 rounded"><ZoomOut size={14} /></button>
          <span className="text-xs font-mono w-12 text-center">{Math.round(zoom * 100)}%</span>
          <button onClick={() => setZoom(z => Math.min(2, z + 0.1))} className="p-1 hover:bg-ink-100 rounded"><ZoomIn size={14} /></button>
        </div>
        <button onClick={() => { if (confirm('Restaurar layout pro padrão?')) restaurar.mutate() }}
          className="btn-secondary flex items-center gap-1.5">
          <RotateCcw size={14} /> Padrão
        </button>
        <button onClick={visualizar} className="btn-secondary flex items-center gap-1.5">
          <Eye size={14} /> Pré-visualizar
        </button>
        <button onClick={() => salvar.mutate()} disabled={salvar.isPending} className="btn-primary flex items-center gap-1.5">
          <Save size={14} /> {salvar.isPending ? 'Salvando...' : 'Salvar'}
        </button>
      </div>

      {/* 3 colunas: lista | canvas | propriedades */}
      <div className="flex-1 flex overflow-hidden">
        {/* Lista de blocos */}
        <div className="w-56 border-r border-ink-200 bg-white overflow-y-auto">
          <div className="px-3 py-2 border-b border-ink-200 text-xs font-bold text-ink-600 uppercase">
            Blocos ({spec.blocos.length})
          </div>
          {spec.blocos.map(b => (
            <button key={b.id}
              onClick={() => setSelecionado(b.id)}
              className={`w-full text-left px-3 py-1.5 text-xs border-b border-ink-100 flex items-center gap-2
                ${selecionado === b.id ? 'bg-brand-50 border-l-2 border-l-brand-500' : 'hover:bg-ink-50'}`}>
              <span className="w-2 h-2 rounded-full" style={{ background: CORES_TIPO[b.tipo] }} />
              <span className="flex-1 truncate font-mono">{b.id}</span>
              <span className="text-ink-400 text-[10px] uppercase">{b.tipo}</span>
            </button>
          ))}
        </div>

        {/* Canvas */}
        <div className="flex-1 overflow-auto bg-ink-100 p-8">
          <Canvas spec={spec} zoom={zoom} selecionado={selecionado}
            onSelecionar={setSelecionado} onAtualizar={atualizarBloco} />
        </div>

        {/* Propriedades */}
        <div className="w-72 border-l border-ink-200 bg-white overflow-y-auto">
          {blocoSelecionado ? (
            <PropriedadesPanel
              bloco={blocoSelecionado}
              onChange={(patch) => atualizarBloco(blocoSelecionado.id, patch)}
              onDeletar={() => deletarBloco(blocoSelecionado.id)}
              campos={campos}
              buscaCampo={buscaCampo}
              setBuscaCampo={setBuscaCampo}
              logoData={logoData}
              setLogoData={setLogoData}
            />
          ) : (
            <div className="p-4 text-xs text-ink-500">
              Clique num bloco no canvas ou na lista pra editar suas propriedades.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// =================== Canvas ===================
function Canvas({ spec, zoom, selecionado, onSelecionar, onAtualizar }: {
  spec: Spec; zoom: number; selecionado: string | null
  onSelecionar: (id: string | null) => void
  onAtualizar: (id: string, patch: Partial<Bloco>) => void
}) {
  const escala = 25 * zoom   // px por cm
  const pageW = PAGINA_LARGURA_CM * escala
  const pageH = PAGINA_ALTURA_CM * escala
  const ref = useRef<HTMLDivElement>(null)

  const [drag, setDrag] = useState<{ id: string; mode: 'move' | 'resize';
    startX: number; startY: number; orig: Bloco } | null>(null)

  const onMouseDown = (e: React.MouseEvent, bloco: Bloco, mode: 'move' | 'resize') => {
    e.stopPropagation(); e.preventDefault()
    onSelecionar(bloco.id)
    setDrag({ id: bloco.id, mode, startX: e.clientX, startY: e.clientY, orig: { ...bloco } as Bloco })
  }

  useEffect(() => {
    if (!drag) return
    const onMove = (e: MouseEvent) => {
      const dxCm = (e.clientX - drag.startX) / escala
      const dyCm = (e.clientY - drag.startY) / escala
      const o = drag.orig
      if (drag.mode === 'move') {
        onAtualizar(drag.id, {
          x: Math.max(0, Math.min(PAGINA_LARGURA_CM - 1, +(o.x + dxCm).toFixed(2))),
          y: Math.max(0, Math.min(PAGINA_ALTURA_CM - 0.2, +(o.y - dyCm).toFixed(2))),
        } as Partial<Bloco>)
      } else {
        onAtualizar(drag.id, {
          w: Math.max(0.5, +(o.w + dxCm).toFixed(2)),
          h: Math.max(0.2, +((o.h || 0.5) + dyCm).toFixed(2)),
        } as Partial<Bloco>)
      }
    }
    const onUp = () => setDrag(null)
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
  }, [drag, escala, onAtualizar])

  return (
    <div className="flex justify-center">
      <div ref={ref} onClick={() => onSelecionar(null)}
        className="relative bg-white shadow-xl"
        style={{ width: pageW, height: pageH, cursor: 'default' }}>
        {/* Régua grosseira */}
        <div className="absolute inset-0 pointer-events-none opacity-10"
          style={{ backgroundImage: `linear-gradient(to right, #94a3b8 1px, transparent 1px),
                                     linear-gradient(to bottom, #94a3b8 1px, transparent 1px)`,
                   backgroundSize: `${escala}px ${escala}px` }} />
        {spec.blocos.map(b => {
          const css = blocoCss(b, escala)
          const sel = selecionado === b.id
          return (
            <div key={b.id}
              onMouseDown={(e) => onMouseDown(e, b, 'move')}
              style={{ position: 'absolute', ...css, cursor: 'move',
                       border: sel ? '2px solid #0ea5e9' : '1px dashed rgba(0,0,0,.25)',
                       background: sel ? 'rgba(14,165,233,.08)' : 'rgba(0,0,0,.02)' }}
              className="overflow-hidden">
              <BlocoVisual bloco={b} escala={escala} />
              {sel && (
                <div
                  onMouseDown={(e) => onMouseDown(e, b, 'resize')}
                  style={{ position: 'absolute', right: -6, bottom: -6, width: 12, height: 12,
                           background: '#0ea5e9', borderRadius: 2, cursor: 'se-resize' }} />
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function blocoCss(b: Bloco, escala: number) {
  // y do spec é bottom-left em cm → CSS top-left em px
  const cssY = (PAGINA_ALTURA_CM - b.y - (b.h || 0.3)) * escala
  return {
    left: b.x * escala,
    top: cssY,
    width: b.w * escala,
    height: (b.h || 0.3) * escala,
  }
}

function BlocoVisual({ bloco, escala }: { bloco: Bloco; escala: number }) {
  if (bloco.tipo === 'texto') {
    const ts = (bloco.fonte?.tamanho || 9) * escala / 28  // aprox: pt → px
    return (
      <div style={{ width: '100%', height: '100%', display: 'flex',
                    alignItems: 'flex-end', padding: '0 2px',
                    justifyContent: bloco.alinhamento === 'right' ? 'flex-end' :
                                    bloco.alinhamento === 'center' ? 'center' : 'flex-start',
                    color: bloco.cor || '#000',
                    fontSize: ts, fontFamily: bloco.fonte?.familia?.includes('Bold') ? 'inherit' : 'inherit',
                    fontWeight: bloco.fonte?.familia?.includes('Bold') ? 700 : 400,
                    fontStyle: bloco.fonte?.familia?.includes('Oblique') ? 'italic' : 'normal' }}>
        <span className="truncate">{bloco.conteudo || '(texto)'}</span>
      </div>
    )
  }
  if (bloco.tipo === 'imagem') {
    return (
      <div className="w-full h-full flex items-center justify-center bg-purple-50 text-purple-700">
        <ImageIcon size={Math.min(escala / 2, 40)} />
      </div>
    )
  }
  if (bloco.tipo === 'linha') {
    return <div style={{ width: '100%', height: 1, background: bloco.cor || '#000', alignSelf: 'center', marginTop: 'auto', marginBottom: 'auto' }} />
  }
  if (bloco.tipo === 'retangulo') {
    return <div className="w-full h-full" style={{ background: bloco.preenchimento || 'transparent',
      border: bloco.preenchimento ? 'none' : `1px solid ${bloco.cor || '#000'}` }} />
  }
  if (bloco.tipo === 'tabela') {
    const colunas = bloco.colunas || []
    return (
      <div className="w-full h-full overflow-hidden flex flex-col">
        <div className="flex" style={{ background: bloco.cor_cabecalho || '#0284c7',
                                       color: bloco.cor_texto_cabecalho || '#fff',
                                       fontSize: Math.max(6, escala * 0.25) }}>
          {colunas.map((c, i) => (
            <div key={i} className="px-1 truncate font-bold" style={{ flex: c.largura }}>{c.label}</div>
          ))}
        </div>
        <div className="text-ink-400 italic text-[10px] p-1">(itens do pedido)</div>
      </div>
    )
  }
  return null
}

// =================== Painel de propriedades ===================
function PropriedadesPanel({ bloco, onChange, onDeletar, campos, buscaCampo, setBuscaCampo, logoData, setLogoData }: {
  bloco: Bloco
  onChange: (patch: Partial<Bloco>) => void
  onDeletar: () => void
  campos: Array<{ campo: string; descricao: string }>
  buscaCampo: string
  setBuscaCampo: (s: string) => void
  logoData: string | null
  setLogoData: (s: string | null) => void
}) {
  const fileRef = useRef<HTMLInputElement>(null)

  return (
    <div className="p-3 space-y-3 text-sm">
      <div className="flex items-center justify-between border-b border-ink-200 pb-2">
        <div>
          <div className="font-bold capitalize">{bloco.tipo}</div>
          <div className="text-xs text-ink-500 font-mono">{bloco.id}</div>
        </div>
        <button onClick={onDeletar} className="text-red-600 hover:bg-red-50 p-1 rounded" title="Remover bloco"><Trash2 size={14} /></button>
      </div>

      {/* Posição/tamanho */}
      <div className="grid grid-cols-2 gap-2">
        <NumberInput label="X (cm)" value={bloco.x} onChange={(v: any) => onChange({ x: v } as Partial<Bloco>)} step={0.1} />
        <NumberInput label="Y (cm)" value={bloco.y} onChange={(v: any) => onChange({ y: v } as Partial<Bloco>)} step={0.1} />
        <NumberInput label="Largura (cm)" value={bloco.w} onChange={(v: any) => onChange({ w: v } as Partial<Bloco>)} step={0.1} min={0.5} />
        <NumberInput label="Altura (cm)" value={bloco.h ?? 0.3} onChange={(v: any) => onChange({ h: v } as Partial<Bloco>)} step={0.1} min={0.2} />
      </div>

      {/* Texto */}
      {bloco.tipo === 'texto' && (
        <>
          <div>
            <label className="text-xs font-medium block mb-1">Conteúdo</label>
            <textarea className="input min-h-[60px] text-xs font-mono"
              value={bloco.conteudo}
              onChange={(e) => onChange({ conteudo: e.target.value } as Partial<Bloco>)} />
            <p className="text-[10px] text-ink-500 mt-1">Use {`{{campo.nome}}`} pra inserir dados. Filtros: {`{{ pedido.total | moeda }}`}</p>
          </div>
          <CatalogoCampos campos={campos} busca={buscaCampo} setBusca={setBuscaCampo}
            onInserir={(c) => onChange({ conteudo: (bloco.conteudo || '') + `{{${c}}}` } as Partial<Bloco>)} />
          <div className="grid grid-cols-2 gap-2">
            <SelectInput label="Família"
              value={bloco.fonte?.familia || 'Helvetica'}
              onChange={(v: any) => onChange({ fonte: { ...bloco.fonte, familia: v } } as Partial<Bloco>)}
              opts={['Helvetica', 'Helvetica-Bold', 'Helvetica-Oblique', 'Helvetica-BoldOblique', 'Times-Roman', 'Times-Bold', 'Courier']} />
            <NumberInput label="Tam. (pt)" value={bloco.fonte?.tamanho || 9}
              onChange={(v: any) => onChange({ fonte: { ...bloco.fonte, tamanho: v } } as Partial<Bloco>)} step={0.5} min={5} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <ColorInput label="Cor" value={bloco.cor || '#000000'} onChange={(v: any) => onChange({ cor: v } as Partial<Bloco>)} />
            <SelectInput label="Alinhamento" value={bloco.alinhamento || 'left'}
              onChange={(v: any) => onChange({ alinhamento: v as 'left' | 'center' | 'right' } as Partial<Bloco>)}
              opts={['left', 'center', 'right']} />
          </div>
        </>
      )}

      {/* Imagem */}
      {bloco.tipo === 'imagem' && (
        <div className="space-y-2">
          <div>
            <label className="text-xs font-medium block mb-1">Fonte</label>
            <select className="input text-xs"
              value={bloco.fonte_imagem || 'empresa.logo'}
              onChange={(e) => onChange({ fonte_imagem: e.target.value } as Partial<Bloco>)}>
              <option value="empresa.logo">Logo da empresa (config)</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium block mb-1">Logo personalizado</label>
            {logoData ? (
              <div className="space-y-1">
                <img src={logoData} className="w-full h-20 object-contain border rounded" alt="Logo" />
                <button onClick={() => setLogoData(null)} className="text-xs text-red-600">Remover</button>
              </div>
            ) : (
              <button onClick={() => fileRef.current?.click()} className="btn-secondary text-xs w-full">
                Enviar PNG/JPG (até 500KB)
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/*" className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0]; if (!f) return
                if (f.size > 500 * 1024) { toast.error('Máx 500KB'); return }
                const reader = new FileReader()
                reader.onload = () => setLogoData(String(reader.result))
                reader.readAsDataURL(f)
              }} />
          </div>
        </div>
      )}

      {/* Linha */}
      {bloco.tipo === 'linha' && (
        <div className="grid grid-cols-2 gap-2">
          <ColorInput label="Cor" value={bloco.cor || '#000000'} onChange={(v: any) => onChange({ cor: v } as Partial<Bloco>)} />
          <NumberInput label="Espessura" value={bloco.espessura || 0.5}
            onChange={(v: any) => onChange({ espessura: v } as Partial<Bloco>)} step={0.1} min={0.1} />
        </div>
      )}

      {/* Retângulo */}
      {bloco.tipo === 'retangulo' && (
        <div className="grid grid-cols-2 gap-2">
          <ColorInput label="Borda" value={bloco.cor || '#000000'} onChange={(v: any) => onChange({ cor: v } as Partial<Bloco>)} />
          <ColorInput label="Fundo" value={bloco.preenchimento || '#ffffff'}
            onChange={(v: any) => onChange({ preenchimento: v } as Partial<Bloco>)} />
        </div>
      )}

      {/* Tabela */}
      {bloco.tipo === 'tabela' && (
        <TabelaPropriedades bloco={bloco} onChange={onChange} campos={campos} />
      )}
    </div>
  )
}

function TabelaPropriedades({ bloco, onChange, campos }: {
  bloco: Extract<Bloco, { tipo: 'tabela' }>
  onChange: (patch: Partial<Bloco>) => void
  campos: Array<{ campo: string; descricao: string }>
}) {
  const camposItem = campos.filter(c => c.campo.startsWith('produto.') || c.campo.startsWith('item.'))
  const editarCol = (idx: number, patch: any) => {
    const novas = bloco.colunas.map((c, i) => i === idx ? { ...c, ...patch } : c)
    onChange({ colunas: novas } as Partial<Bloco>)
  }
  const remover = (idx: number) => {
    onChange({ colunas: bloco.colunas.filter((_, i) => i !== idx) } as Partial<Bloco>)
  }
  const adicionar = () => {
    onChange({ colunas: [...bloco.colunas, { label: 'Nova', campo: 'item.total', largura: 2, align: 'right' }] } as Partial<Bloco>)
  }
  const mover = (idx: number, delta: number) => {
    const arr = [...bloco.colunas]
    const novaIdx = idx + delta
    if (novaIdx < 0 || novaIdx >= arr.length) return
    ;[arr[idx], arr[novaIdx]] = [arr[novaIdx], arr[idx]]
    onChange({ colunas: arr } as Partial<Bloco>)
  }
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <ColorInput label="Cor cabeçalho" value={bloco.cor_cabecalho || '#0284c7'}
          onChange={(v: any) => onChange({ cor_cabecalho: v } as Partial<Bloco>)} />
        <ColorInput label="Texto cabeçalho" value={bloco.cor_texto_cabecalho || '#ffffff'}
          onChange={(v: any) => onChange({ cor_texto_cabecalho: v } as Partial<Bloco>)} />
      </div>
      <div>
        <div className="flex items-center justify-between">
          <label className="text-xs font-medium">Colunas</label>
          <button onClick={adicionar} className="text-xs text-brand-700 flex items-center gap-0.5"><Plus size={10} /> add</button>
        </div>
        <div className="space-y-1 mt-1">
          {bloco.colunas.map((c, i) => (
            <div key={i} className="border border-ink-200 rounded p-2 space-y-1 text-xs">
              <div className="flex gap-1">
                <input className="input !py-1 flex-1 !text-xs" value={c.label}
                  onChange={(e) => editarCol(i, { label: e.target.value })} placeholder="Label" />
                <button onClick={() => mover(i, -1)} className="px-1 text-ink-400 hover:text-ink-700" title="↑">▲</button>
                <button onClick={() => mover(i, 1)} className="px-1 text-ink-400 hover:text-ink-700" title="↓">▼</button>
                <button onClick={() => remover(i)} className="text-red-600"><Trash2 size={12} /></button>
              </div>
              <select className="input !py-1 !text-xs" value={c.campo}
                onChange={(e) => editarCol(i, { campo: e.target.value })}>
                {camposItem.map(cm => <option key={cm.campo} value={cm.campo}>{cm.campo} — {cm.descricao}</option>)}
              </select>
              <div className="grid grid-cols-3 gap-1">
                <input type="number" step="0.1" min="0.5" className="input !py-1 !text-xs"
                  value={c.largura} onChange={(e) => editarCol(i, { largura: parseFloat(e.target.value) })}
                  title="Largura (cm)" />
                <select className="input !py-1 !text-xs" value={c.align || 'left'}
                  onChange={(e) => editarCol(i, { align: e.target.value })}>
                  <option value="left">esq</option><option value="center">cen</option><option value="right">dir</option>
                </select>
                <select className="input !py-1 !text-xs" value={c.formato || ''}
                  onChange={(e) => editarCol(i, { formato: e.target.value || undefined })}>
                  <option value="">—</option>
                  <option value="moeda">R$</option>
                  <option value="data">data</option>
                </select>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function CatalogoCampos({ campos, busca, setBusca, onInserir }: {
  campos: Array<{ campo: string; descricao: string }>
  busca: string; setBusca: (s: string) => void
  onInserir: (campo: string) => void
}) {
  const filt = campos.filter(c => !busca || c.campo.includes(busca.toLowerCase()) ||
    c.descricao.toLowerCase().includes(busca.toLowerCase()))
  return (
    <div className="border border-ink-200 rounded">
      <div className="flex items-center gap-1 border-b border-ink-200 px-2">
        <Search size={12} className="text-ink-400" />
        <input className="text-xs py-1 flex-1 outline-none" placeholder="Buscar campo..."
          value={busca} onChange={(e) => setBusca(e.target.value)} />
      </div>
      <div className="max-h-40 overflow-y-auto text-xs">
        {filt.slice(0, 30).map(c => (
          <button key={c.campo} onClick={() => onInserir(c.campo)}
            className="w-full text-left px-2 py-1 border-b border-ink-100 hover:bg-brand-50 flex justify-between gap-2">
            <span className="font-mono text-brand-700 truncate">{c.campo}</span>
            <span className="text-ink-500 truncate max-w-[120px]">{c.descricao}</span>
          </button>
        ))}
      </div>
    </div>
  )
}

// =================== Inputs base ===================
function NumberInput({ label, value, onChange, step, min }: any) {
  return (
    <label className="block">
      <span className="text-[10px] font-medium text-ink-600 uppercase">{label}</span>
      <input type="number" step={step || 1} min={min}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        className="input !py-1 !text-xs w-full" />
    </label>
  )
}

function ColorInput({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <label className="block">
      <span className="text-[10px] font-medium text-ink-600 uppercase">{label}</span>
      <div className="flex items-center gap-1">
        <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="h-7 w-7 rounded border" />
        <input type="text" value={value} onChange={(e) => onChange(e.target.value)}
          className="input !py-1 !text-xs flex-1 font-mono" />
      </div>
    </label>
  )
}

function SelectInput({ label, value, onChange, opts }: { label: string; value: string; onChange: (v: string) => void; opts: string[] }) {
  return (
    <label className="block">
      <span className="text-[10px] font-medium text-ink-600 uppercase">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="input !py-1 !text-xs w-full">
        {opts.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  )
}

// =================== Menu adicionar bloco ===================
function AdicionarBlocoMenu({ onAdicionar }: { onAdicionar: (t: Bloco['tipo']) => void }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const onDoc = (e: MouseEvent) => { if (open && ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('mousedown', onDoc); return () => document.removeEventListener('mousedown', onDoc)
  }, [open])
  const opcoes: Array<[Bloco['tipo'], string, any]> = [
    ['texto', 'Texto', Type], ['imagem', 'Imagem', ImageIcon],
    ['linha', 'Linha', Minus], ['retangulo', 'Retângulo', Square],
    ['tabela', 'Tabela de itens', Table],
  ]
  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(o => !o)} className="btn-secondary flex items-center gap-1.5">
        <Plus size={14} /> Adicionar <ChevronDown size={12} />
      </button>
      {open && (
        <div className="absolute top-full mt-1 left-0 bg-white border border-ink-200 rounded-lg shadow-lg w-44 z-30">
          {opcoes.map(([k, label, Icon]) => (
            <button key={k} onClick={() => { onAdicionar(k); setOpen(false) }}
              className="w-full text-left px-3 py-2 text-xs hover:bg-brand-50 flex items-center gap-2">
              <Icon size={14} style={{ color: CORES_TIPO[k] }} /> {label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// =================== Seletor de templates ===================
function TemplateSeletor({ templates, atual, onTrocar, onAtivar, onCriar, onDeletar }: {
  templates: TemplateItem[]
  atual: string
  onTrocar: (nome: string) => void
  onAtivar: (nome: string) => void
  onCriar: (nome: string, copiar?: string) => void
  onDeletar: (nome: string) => void
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const onDoc = (e: MouseEvent) => { if (open && ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('mousedown', onDoc); return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  const ativo = templates.find(t => t.ativo)
  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(o => !o)}
        className="px-2 py-1 text-xs border border-ink-300 rounded bg-white hover:bg-ink-50 flex items-center gap-1.5">
        <span className="text-ink-500">Template:</span>
        <span className="font-medium">{atual || ativo?.nome || 'Padrão'}</span>
        {atual && templates.find(t => t.nome === atual)?.ativo && (
          <span className="text-emerald-600" title="Em uso nos PDFs"><Check size={12} /></span>
        )}
        <ChevronDown size={12} />
      </button>
      {open && (
        <div className="absolute top-full mt-1 left-0 bg-white border border-ink-200 rounded-lg shadow-lg w-72 z-30">
          <div className="border-b border-ink-200 max-h-64 overflow-y-auto">
            {templates.map(t => (
              <div key={t.id} className={`px-3 py-1.5 text-xs border-b border-ink-100 hover:bg-ink-50
                ${atual === t.nome ? 'bg-brand-50' : ''}`}>
                <div className="flex items-center justify-between gap-2">
                  <button className="flex-1 text-left flex items-center gap-1.5"
                    onClick={() => { onTrocar(t.nome); setOpen(false) }}>
                    {t.ativo && <Check size={12} className="text-emerald-600" />}
                    <span className={`${atual === t.nome ? 'font-semibold' : ''}`}>{t.nome}</span>
                    {!t.tem_spec && <span className="text-[10px] text-amber-600">(sem spec)</span>}
                  </button>
                  {!t.ativo && (
                    <button onClick={(e) => { e.stopPropagation(); onAtivar(t.nome) }}
                      className="text-[10px] text-emerald-700 hover:underline">ativar</button>
                  )}
                  {templates.length > 1 && (
                    <button onClick={(e) => { e.stopPropagation(); onDeletar(t.nome) }}
                      className="text-red-600 hover:bg-red-50 rounded p-0.5"><Trash2 size={10} /></button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="p-2 space-y-1">
            <button onClick={() => {
              const n = window.prompt('Nome do novo template:', 'Promocional')
              if (n) { onCriar(n.trim()); setOpen(false) }
            }}
              className="w-full text-xs px-2 py-1.5 hover:bg-brand-50 text-brand-700 rounded flex items-center gap-1.5">
              <Plus size={12} /> Novo template (em branco)
            </button>
            <button onClick={() => {
              const base = atual || ativo?.nome
              if (!base) { toast.error('Selecione um template como base'); return }
              const n = window.prompt(`Duplicar "${base}" como:`, `${base} (cópia)`)
              if (n) { onCriar(n.trim(), base); setOpen(false) }
            }}
              className="w-full text-xs px-2 py-1.5 hover:bg-brand-50 text-brand-700 rounded flex items-center gap-1.5">
              <Copy size={12} /> Duplicar o atual
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
